import React, { useState, useEffect, useRef } from 'react';
import DynamicField from './DynamicField';
import airtableService from '../services/airtableService';
import { getFieldConfig } from '../config/formFieldsConfig';
import { config } from '../config/config';
import { filterConditionalFields } from '../utils/conditionalFields';
import { getFormTypeForPosition, getFormTypeDisplayName } from '../config/positionFormMapping';
import { getFormFields } from '../config/formDefinitions';
import { getCitiesForCountry } from '../config/countryCityMapping';

/**
 * Tab 2: Complete Your Profile
 * All remaining fields after CV upload and parsing
 */
const Tab2_CompleteProfile = ({ formData, onChange, errors, files, onFileChange }) => {
  const [dynamicFields, setDynamicFields] = useState([]);
  const [positionFields, setPositionFields] = useState([]);
  const [positionFormType, setPositionFormType] = useState(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadDynamicFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load position-specific fields when position changes
  useEffect(() => {
    if (formData.position) {
      loadPositionSpecificFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.position]);

  // Update portfolio field required status when form type changes
  useEffect(() => {
    if (dynamicFields.length > 0) {
      const portfolioRequiredFormTypes = [
        'Video/Photo',
        'Production',
        'Creative',
        'Tech',
        'Tech Leadership',
        'Post Production',
        'Social Media'
      ];

      setDynamicFields(prevFields =>
        prevFields.map(field => {
          if (field.fieldKey === 'portfolio') {
            return {
              ...field,
              required: positionFormType && portfolioRequiredFormTypes.includes(positionFormType)
            };
          }
          return field;
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionFormType]);

  // Re-render when formData changes (for conditional fields)
  // This doesn't reload the fields, just triggers a re-render
  useEffect(() => {
    // This effect intentionally empty - just triggers re-render when formData changes
  }, [formData]);

  const loadDynamicFields = async () => {
    try {
      const airtableFields = await airtableService.getDynamicFormFields(config.airtable.tables.shortFormApplicant);

      const configuredFields = [];

      // Fields to exclude (already in Tab 1)
      const excludedFields = ['email', 'position', 'fullpart_time', 'cv'];

      for (const airtableField of airtableFields) {
        // Skip fields that are in Tab 1
        if (excludedFields.includes(airtableField.fieldKey)) {
          continue;
        }

        const fieldConfig = getFieldConfig(airtableField.fieldKey);

        // Skip fields not in config
        if (!fieldConfig) continue;

        // Enhance field with config data
        const enhancedField = {
          ...airtableField,
          name: fieldConfig.displayName || airtableField.name,
          placeholder: fieldConfig.placeholder || airtableField.placeholder,
          required: fieldConfig.required !== undefined ? fieldConfig.required : airtableField.required,
          helpText: fieldConfig.helpText || airtableField.description,
          order: fieldConfig.order,
          section: fieldConfig.section
        };

        // Fetch linked record options if needed
        if (enhancedField.fetchLinkedOptions && enhancedField.linkedTable) {
          try {
            const options = await airtableService.getLinkedTableRecords(enhancedField.linkedTable);
            enhancedField.options = options;
          } catch (error) {
            console.error(`Error fetching options for ${enhancedField.name}:`, error);
          }
        }

        // Special handling for Gender field
        if (enhancedField.fieldKey === 'gmf' && enhancedField.options) {
          enhancedField.options = enhancedField.options.map(option => {
            if (option === 'M') return { value: 'M', label: 'Male' };
            if (option === 'F') return { value: 'F', label: 'Female' };
            return typeof option === 'string' ? { value: option, label: option } : option;
          });
        }

        // Special handling for Language fields
        if (enhancedField.fieldKey === 'gspoken_languages' || enhancedField.fieldKey === 'gwritten_languages') {
          enhancedField.type = 'multiselect';
          // Use linked record options from Airtable (includes record IDs)
          // enhancedField.options = config.options.languages; // OLD: Used text values
          // Options are already loaded from getLinkedTableRecords above
        }

        // Special handling for Nationality field - make it multiselect
        if (enhancedField.fieldKey === 'nationality') {
          enhancedField.type = 'multiselect';
          // Keep the full object structure {label, value} for linked records
          // The MultiSelect component will handle this format
        }

        // Special handling for Portfolio field
        if (enhancedField.fieldKey === 'portfolio') {
          enhancedField.type = 'url';
          // Note: required status will be updated based on position form type via useEffect
        }

        // Date of Birth field uses the custom DatePicker (type='date' from config)

        // Filter out placeholder/invalid options
        if (enhancedField.options && Array.isArray(enhancedField.options)) {
          enhancedField.options = enhancedField.options.filter(option => {
            const optionValue = typeof option === 'string' ? option : option.label || option.value;
            const isInvalid = config.validation.invalidValuePatterns.some(pattern =>
              optionValue && optionValue.toString().toLowerCase().includes(pattern)
            );
            return !isInvalid;
          });
        }

        configuredFields.push(enhancedField);
      }

      // Sort fields by order
      configuredFields.sort((a, b) => a.order - b.order);

      setDynamicFields(configuredFields);

    } catch (error) {
      console.error('Error loading dynamic fields:', error.response?.data || error.message, error);
      setDynamicFields([]);
    }
  };

  const loadPositionSpecificFields = async () => {
    try {
      if (!formData.position) {
        setPositionFormType(null);
        setPositionFields([]);
        return;
      }

      // Use new mapping system: position → form type
      const formType = getFormTypeForPosition(formData.position);

      if (!formType) {
        setPositionFormType(null);
        setPositionFields([]);
        return;
      }

      const formTypeDisplayName = getFormTypeDisplayName(formType);
      setPositionFormType(formTypeDisplayName);

      // Get form-specific fields from new definitions
      const fields = getFormFields(formType);

      // Convert to format compatible with DynamicField component
      const formattedFields = fields.map((field, index) => {
        return {
          id: field.id || `form_field_${index}`,
          name: field.label,
          fieldKey: field.name,
          type: field.type,
          required: field.required || false,
          placeholder: field.placeholder || '',
          helpText: field.helpText || '',
          options: field.options || [],
          order: 1000 + index,
          section: 'Bring Your A-GAME!',
          // Add conditional dependency if present
          dependsOn: field.dependsOn || undefined
        };
      });

      setPositionFields(formattedFields);

    } catch (error) {
      console.error('Error loading position-specific fields:', error);
      setPositionFields([]);
    }
  };

  const isFieldFilled = (fieldName) => {
    const value = formData[fieldName];

    if (!value || value === '' || value === null || value === undefined) {
      return false;
    }

    if (Array.isArray(value) && value.length === 0) {
      return false;
    }

    const valueStr = value.toString().toLowerCase().trim();
    const isPlaceholder = config.validation.invalidValuePatterns.some(pattern =>
      valueStr.includes(pattern)
    );

    return !isPlaceholder;
  };

  const isAutoFilled = (fieldName) => {
    return formData[fieldName] && formData._autoFilled?.includes(fieldName);
  };

  const isFullWidthField = (field) => {
    const fieldNameLower = field.name.toLowerCase();

    const hasFullWidthKeyword = config.validation.fullWidthKeywords.some(keyword =>
      fieldNameLower.includes(keyword)
    );

    const isCheckbox = field.type === 'checkbox';
    const isAgreementSection = field.section === 'Agreement';

    return hasFullWidthKeyword || isCheckbox || isAgreementSection;
  };

  if (dynamicFields.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
        <p>Loading form fields...</p>
      </div>
    );
  }

  // Filter cities based on selected country using JSON mapping as single source of truth
  const getFilteredFieldOptions = (field) => {
    // If this is the city field and a country is selected, get cities from JSON
    if (field.fieldKey === 'gcity' && formData.where_do_you_currently_reside) {
      const selectedCountry = formData.where_do_you_currently_reside;

      // Get the country name (it could be stored as the label or as a record ID)
      let countryName = selectedCountry;

      // If selectedCountry is a record ID, find the country label from options
      if (selectedCountry && selectedCountry.startsWith('rec')) {
        // Find the country field to get its options
        const countryField = dynamicFields.find(f => f.fieldKey === 'where_do_you_currently_reside');

        if (countryField && countryField.options) {
          const countryOption = countryField.options.find(opt => opt.value === selectedCountry || opt.id === selectedCountry);
          if (countryOption) {
            countryName = countryOption.label;
          }
        }
      }

      // Get cities for the selected country from JSON mapping (single source of truth)
      const cities = getCitiesForCountry(countryName);

      if (cities.length > 0) {
        // Convert cities to options format
        const citiesAsOptions = cities.map(city => ({
          value: city,
          label: city
        }));

        return {
          ...field,
          options: citiesAsOptions,
          placeholder: 'Select city'
        };
      } else {
        // No cities found for this country in JSON
        return {
          ...field,
          options: [],
          placeholder: 'No cities available for selected country'
        };
      }
    }

    return field;
  };

  // Render fields with proper layout
  const renderFields = () => {
    const elements = [];
    let currentSection = null;
    let rowBuffer = [];

    dynamicFields.forEach((field, index) => {
      // Apply filtering for dependent fields (like cities based on country)
      const processedField = getFilteredFieldOptions(field);
      // Add section header if section changed
      if (processedField.section !== currentSection) {
        // Flush buffered fields
        if (rowBuffer.length > 0) {
          elements.push(
            <div key={`row-${elements.length}`} className="ast-contact-form-row">
              {rowBuffer.map((bufferedField, bufIndex) => (
                <DynamicField
                  key={`${bufferedField.fieldKey}-${bufferedField.options?.length || 0}-${bufIndex}`}
                  field={bufferedField}
                  value={formData[bufferedField.fieldKey]}
                  onChange={onChange}
                  error={errors[bufferedField.fieldKey]}
                  autoFilled={isAutoFilled(bufferedField.fieldKey)}
                  isFulfilled={isFieldFilled(bufferedField.fieldKey)}
                />
              ))}
            </div>
          );
          rowBuffer = [];
        }

        currentSection = processedField.section;
      }

      const isFullWidth = isFullWidthField(processedField);

      // Flush buffer if current field is full width
      if (isFullWidth && rowBuffer.length > 0) {
        elements.push(
          <div key={`row-${elements.length}`} className="ast-contact-form-row">
            {rowBuffer.map((bufferedField, bufIndex) => (
              <DynamicField
                key={bufferedField.id || `buffered-${bufIndex}`}
                field={bufferedField}
                value={formData[bufferedField.fieldKey]}
                onChange={onChange}
                error={errors[bufferedField.fieldKey]}
                autoFilled={isAutoFilled(bufferedField.fieldKey)}
                isFulfilled={isFieldFilled(bufferedField.fieldKey)}
              />
            ))}
          </div>
        );
        rowBuffer = [];
      }

      if (isFullWidth) {
        // Render full-width field immediately
        elements.push(
          <div key={processedField.id || index} className="ast-contact-form-row" style={{ gridTemplateColumns: '1fr' }}>
            <DynamicField
              key={`${processedField.fieldKey}-${processedField.options?.length || 0}`}
              field={processedField}
              value={formData[processedField.fieldKey]}
              onChange={onChange}
              error={errors[processedField.fieldKey]}
              autoFilled={isAutoFilled(processedField.fieldKey)}
              isFulfilled={isFieldFilled(processedField.fieldKey)}
            />
          </div>
        );
      } else {
        // Buffer PROCESSED field (not original) for 2-column row
        rowBuffer.push(processedField);

        // Flush when buffer has 2 fields
        if (rowBuffer.length === 2) {
          elements.push(
            <div key={`row-${elements.length}`} className="ast-contact-form-row">
              {rowBuffer.map((bufferedField, bufIndex) => (
                <DynamicField
                  key={`${bufferedField.fieldKey}-${bufferedField.options?.length || 0}-${bufIndex}`}
                  field={bufferedField}
                  value={formData[bufferedField.fieldKey]}
                  onChange={onChange}
                  error={errors[bufferedField.fieldKey]}
                  autoFilled={isAutoFilled(bufferedField.fieldKey)}
                  isFulfilled={isFieldFilled(bufferedField.fieldKey)}
                />
              ))}
            </div>
          );
          rowBuffer = [];
        }
      }
    });

    // Flush remaining fields
    if (rowBuffer.length > 0) {
      elements.push(
        <div key={`row-${elements.length}`} className="ast-contact-form-row">
          {rowBuffer.map((bufferedField, bufIndex) => (
            <DynamicField
              key={bufferedField.id || `buffered-${bufIndex}`}
              field={bufferedField}
              value={formData[bufferedField.fieldKey]}
              onChange={onChange}
              error={errors[bufferedField.fieldKey]}
              autoFilled={isAutoFilled(bufferedField.fieldKey)}
              isFulfilled={isFieldFilled(bufferedField.fieldKey)}
            />
          ))}
        </div>
      );
    }

    return elements;
  };

  // Render position-specific fields
  const renderPositionFields = () => {
    if (positionFields.length === 0) {
      return null;
    }

    // Filter fields based on conditional visibility
    const visibleFields = filterConditionalFields(positionFields, formData);

    const elements = [];
    let rowBuffer = [];

    visibleFields.forEach((field, index) => {
      const isFullWidth = isFullWidthField(field);

      // Flush buffer if current field is full width
      if (isFullWidth && rowBuffer.length > 0) {
        elements.push(
          <div key={`row-${elements.length}`} className="ast-contact-form-row">
            {rowBuffer.map((bufferedField, bufIndex) => (
              <DynamicField
                key={bufferedField.id || `buffered-${bufIndex}`}
                field={bufferedField}
                value={formData[bufferedField.fieldKey]}
                onChange={onChange}
                error={errors[bufferedField.fieldKey]}
                autoFilled={isAutoFilled(bufferedField.fieldKey)}
                isFulfilled={isFieldFilled(bufferedField.fieldKey)}
              />
            ))}
          </div>
        );
        rowBuffer = [];
      }

      if (isFullWidth) {
        // Render full-width field
        elements.push(
          <div key={field.id || index} className="ast-contact-form-row" style={{ gridTemplateColumns: '1fr' }}>
            <DynamicField
              field={field}
              value={formData[field.fieldKey]}
              onChange={onChange}
              error={errors[field.fieldKey]}
              autoFilled={isAutoFilled(field.fieldKey)}
              isFulfilled={isFieldFilled(field.fieldKey)}
            />
          </div>
        );
      } else {
        // Buffer field for 2-column row
        rowBuffer.push(field);

        // Flush when buffer has 2 fields
        if (rowBuffer.length === 2) {
          elements.push(
            <div key={`row-${elements.length}`} className="ast-contact-form-row">
              {rowBuffer.map((bufferedField, bufIndex) => (
                <DynamicField
                  key={`${bufferedField.fieldKey}-${bufferedField.options?.length || 0}-${bufIndex}`}
                  field={bufferedField}
                  value={formData[bufferedField.fieldKey]}
                  onChange={onChange}
                  error={errors[bufferedField.fieldKey]}
                  autoFilled={isAutoFilled(bufferedField.fieldKey)}
                  isFulfilled={isFieldFilled(bufferedField.fieldKey)}
                />
              ))}
            </div>
          );
          rowBuffer = [];
        }
      }
    });

    // Flush remaining fields
    if (rowBuffer.length > 0) {
      elements.push(
        <div key={`row-${elements.length}`} className="ast-contact-form-row">
          {rowBuffer.map((bufferedField, bufIndex) => (
            <DynamicField
              key={bufferedField.id || `buffered-${bufIndex}`}
              field={bufferedField}
              value={formData[bufferedField.fieldKey]}
              onChange={onChange}
              error={errors[bufferedField.fieldKey]}
              autoFilled={isAutoFilled(bufferedField.fieldKey)}
              isFulfilled={isFieldFilled(bufferedField.fieldKey)}
            />
          ))}
        </div>
      );
    }

    return elements;
  };

  return (
    <div className="tab-content">
      {/* Form Fields */}
      <div className="astudio-form-container">
        {renderFields()}

        {/* Position-Specific Fields Section */}
        {positionFields.length > 0 && (
          <div className="position-specific-section" style={{ marginTop: '60px' }}>
            {renderPositionFields()}
          </div>
        )}
      </div>
    </div>
  );
};

export default Tab2_CompleteProfile;
